import { useEffect, useRef, useState } from 'react';

/**
 * Miniatura de un video sin dejar abiertos decenas de videos a la vez (eso trababa la consola con
 * los videos de EasyWorship). Solo se saca la imagen de los que se ven, de dos en dos; el video se
 * cierra en cuanto da su cuadro y la imagen se guarda para no volver a sacarla al cambiar de pestaña.
 */
const frames = new Map<string, ImageBitmap | 'error'>();
const waiting: (() => void)[] = [];
let running = 0;
const MAX_AT_ONCE = 2;
const WIDTH = 320;
const HEIGHT = 180;

function next() {
  while (running < MAX_AT_ONCE && waiting.length) {
    running += 1;
    waiting.shift()!();
  }
}

function inTurn(task: () => Promise<void>) {
  waiting.push(() =>
    void task().finally(() => {
      running -= 1;
      next();
    }),
  );
  next();
}

/** Abre el video fuera de la página, toma un cuadro cerca del segundo 1 y lo cierra. */
function grabFrame(src: string): Promise<ImageBitmap> {
  return new Promise((resolve, reject) => {
    if (typeof createImageBitmap === 'undefined') return reject(new Error('Sin createImageBitmap'));
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    const close = () => {
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
    };
    const fail = (e: unknown) => {
      close();
      reject(e instanceof Error ? e : new Error('Sin cuadro'));
    };
    const timer = setTimeout(() => fail(new Error('Tardó demasiado')), 15000);
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, (video.duration || 2) / 2);
    };
    video.onseeked = () => {
      createImageBitmap(video, { resizeWidth: WIDTH, resizeHeight: HEIGHT, resizeQuality: 'medium' }).then((bitmap) => {
        close();
        resolve(bitmap);
      }, fail);
    };
    video.onerror = () => fail(new Error('No se pudo abrir el video'));
    video.src = src;
  });
}

export function VideoThumb({ src }: { src: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(frames.get(src) === 'error');

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    let alive = true;
    const paint = (bitmap: ImageBitmap) => el.getContext('2d')?.drawImage(bitmap, 0, 0, WIDTH, HEIGHT);
    const cached = frames.get(src);
    if (cached === 'error') return;
    if (cached) {
      paint(cached);
      return;
    }
    const start = () =>
      inTurn(async () => {
        if (!alive) return;
        const again = frames.get(src);
        if (again && again !== 'error') return void paint(again);
        try {
          const bitmap = await grabFrame(src);
          frames.set(src, bitmap);
          if (alive) paint(bitmap);
        } catch {
          frames.set(src, 'error');
          if (alive) setFailed(true);
        }
      });
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return () => {
        alive = false;
      };
    }
    // Solo los que están a la vista (o casi): con cientos de videos no se abren todos.
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        start();
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => {
      alive = false;
      observer.disconnect();
    };
  }, [src]);

  if (failed) return <span aria-hidden="true">▶</span>;
  return <canvas ref={canvas} width={WIDTH} height={HEIGHT} aria-hidden="true" />;
}
